// Complete Drawer Content Component with Header and Menu
// Ready to use - just pass menu items and handlers

import React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LogOutIcon from '@mui/icons-material/Logout';

import MiniDrawerStyled from './MiniDrawer';
import drawerConfig from '../drawerConfig';

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

/**
 * DrawerContent - Complete drawer with header, menu items, and logout
 * 
 * @param {Object} props
 * @param {boolean} props.open - Drawer open state
 * @param {boolean} props.showScrollbar - Scrollbar visibility
 * @param {Function} props.onToggle - Toggle drawer callback
 * @param {Array} props.menuItems - Array of menu items with { icon, text, link }
 * @param {Function} props.onMenuClick - Menu item click handler
 * @param {Function} props.onLogout - Logout handler
 * @param {Function} props.onMouseMove - Mouse move handler for scrollbar
 * @param {Function} props.onMouseLeave - Mouse leave handler for scrollbar
 */
export default function DrawerContent({
  open,
  showScrollbar,
  onToggle,
  menuItems = [],
  onMenuClick,
  onLogout,
  onMouseMove,
  onMouseLeave,
}) {
  return (
    <Box
      component="nav"
      sx={{
        flexShrink: 0,
        width: drawerConfig.width,
      }}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      <MiniDrawerStyled
        variant="permanent"
        open={open}
        showScrollbar={showScrollbar}
        anchor={drawerConfig.anchor}
      >
        <DrawerHeader>
          {drawerConfig.showMenuIcon && (
            <IconButton onClick={onToggle}>
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          )}
        </DrawerHeader>
        <Divider />
        <List>
          {menuItems.map((link, index) => (
            <Tooltip
              key={link.link || link.text || index}
              title={link.text || ''}
              placement="right"
              disableHoverListener={open}
              disableFocusListener={open}
              disableTouchListener={open}
            >
              <ListItemButton
                onClick={() => onMenuClick(link.link)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: '#ede7f6',
                    display: 'flex',
                    alignItems: 'center',
                    '& svg': {
                      color: '#673ab7',
                      fontSize: '1.5rem'
                    }
                  }}
                >
                  {link.icon}
                </ListItemIcon>
                <ListItemText
                  primary={link.text}
                  primaryTypographyProps={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    whiteSpace: 'normal',
                    wordbreak: 'break-word',
                  }}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </ListItemButton>
            </Tooltip>
          ))}
        </List>
        <Divider />
        <List>
          <Tooltip
            title="Log Out"
            placement="right"
            disableHoverListener={open}
            disableFocusListener={open}
            disableTouchListener={open}
          >
            <ListItemButton
              onClick={onLogout}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: '#ede7f6',
                  display: 'flex',
                  alignItems: 'center',
                  '& svg': {
                    color: '#673ab7',
                    fontSize: '1.5rem'
                  }
                }}
              >
                <LogOutIcon />
              </ListItemIcon>
              <ListItemText
                primary={'Log Out'}
                primaryTypographyProps={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
                sx={{ opacity: open ? 1 : 0 }}
              />
            </ListItemButton>
          </Tooltip>
        </List>
      </MiniDrawerStyled>
    </Box>
  );
}
